import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Eye, ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import AppLayout from "../components/AppLayout";
import CountryCombobox from "../components/CountryCombobox";
import ImageUpload from "../components/ImageUpload";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  BUSINESS_TYPES, PROFESSIONAL_TYPES, INVESTOR_TYPES, COMPANY_SIZES,
  BUSINESS_STAGES, INDUSTRIES, OFFERS, LOOKING_FOR,
} from "../data/hub";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ title, subtitle, children, testId }) {
  return (
    <section data-testid={testId} className="hub-card rounded-2xl p-6 lg:p-8">
      <div className="mb-6">
        <h2 className="font-display text-xl font-semibold text-[color:var(--ink)]">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-[color:var(--ink-soft)]">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function ChipToggle({ options, value = [], onChange, testIdPrefix }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const sel = value.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} data-testid={`${testIdPrefix}-${o}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
              sel ? "border-[color:var(--invert-bg)] bg-[color:var(--invert-bg)] text-white" :
              "border-[color:var(--hairline)] bg-[color:var(--surface)] text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function MyProfile() {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [business, setBusiness] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hub/profile`, { withCredentials: true });
      setProfile(data.profile);
      setBusiness(data.business);
    } catch { toast.error("Failed to load profile."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const kind = profile?.member_type
    ? BUSINESS_TYPES.has(profile.member_type) ? "business"
    : INVESTOR_TYPES.has(profile.member_type) ? "investor"
    : PROFESSIONAL_TYPES.has(profile.member_type) ? "professional" : "other" : "";

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setBiz = (k, v) => setBusiness((b) => ({ ...(b || {}), [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        full_name: profile.full_name, professional_headline: profile.professional_headline, bio: profile.bio,
        country: profile.country, city: profile.city, linkedin_url: profile.linkedin_url, website: profile.website,
        profile_photo: profile.profile_photo, offers: profile.offers, looking_for: profile.looking_for,
        looking_for_details: profile.looking_for_details, discoverable_preference: profile.discoverable_preference,
      };
      if (kind === "professional") payload.professional = profile.professional_info || null;
      if (kind === "investor") payload.investor = profile.investor_info || null;
      await axios.patch(`${API}/hub/profile`, payload, { withCredentials: true });
      if (kind === "business" && business && business.name) {
        await axios.patch(`${API}/hub/business`, business, { withCredentials: true });
      }
      toast.success("Profile saved.");
      await refreshUser();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  if (loading || !profile) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--ink-soft)]" /></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Seo title="My profile" path="/app/profile" />
      <div className="container-hub py-8 lg:py-12" data-testid="my-profile-page">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/app" className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"><ArrowLeft className="h-3.5 w-3.5" /> Back to My Hub</Link>
            <h1 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)]">My profile</h1>
            <p className="mt-1 text-sm text-[color:var(--ink-soft)]">Update your details. Changes save to your account instantly.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app/profile/preview" data-testid="profile-preview-link" className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-5 py-2.5 text-sm font-semibold text-[color:var(--ink)]"><Eye className="h-4 w-4" /> Preview</Link>
            <button onClick={saveProfile} disabled={saving} data-testid="profile-save"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-5 py-2.5 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform disabled:opacity-70">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Save className="h-4 w-4" /> Save</>}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <Section title="Personal information" testId="section-personal">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-[auto_1fr]">
              <ImageUpload value={profile.profile_photo} onChange={(v) => set("profile_photo", v)} testId="profile-photo" />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Full name</Label><Input value={profile.full_name || ""} onChange={(e) => set("full_name", e.target.value)} className="h-11 rounded-xl" data-testid="mp-name" /></div>
                <div className="space-y-2"><Label>Professional headline</Label><Input value={profile.professional_headline || ""} onChange={(e) => set("professional_headline", e.target.value)} className="h-11 rounded-xl" data-testid="mp-headline" /></div>
                <div className="space-y-2"><Label>Country</Label><CountryCombobox value={profile.country || ""} onChange={(v) => set("country", v)} testIdPrefix="mp-country" /></div>
                <div className="space-y-2"><Label>City</Label><Input value={profile.city || ""} onChange={(e) => set("city", e.target.value)} className="h-11 rounded-xl" /></div>
                <div className="space-y-2"><Label>LinkedIn</Label><Input value={profile.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)} className="h-11 rounded-xl" /></div>
                <div className="space-y-2"><Label>Website</Label><Input value={profile.website || ""} onChange={(e) => set("website", e.target.value)} className="h-11 rounded-xl" /></div>
                <div className="space-y-2 sm:col-span-2"><Label>Bio</Label><Textarea value={profile.bio || ""} onChange={(e) => set("bio", e.target.value)} className="min-h-24 rounded-xl" data-testid="mp-bio" /></div>
              </div>
            </div>
          </Section>

          {kind === "business" && business && (
            <Section title="Business information" subtitle={`Managing ${business.name || "your business"}`} testId="section-business">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[auto_1fr]">
                <ImageUpload kind="logo" value={business.logo} onChange={(v) => setBiz("logo", v)} testId="business-logo" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2"><Label>Business name</Label><Input value={business.name || ""} onChange={(e) => setBiz("name", e.target.value)} className="h-11 rounded-xl" data-testid="mp-biz-name" /></div>
                  <div className="space-y-2"><Label>Industry</Label>
                    <Select value={business.industry || ""} onValueChange={(v) => setBiz("industry", v)}>
                      <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Country</Label><CountryCombobox value={business.country || ""} onChange={(v) => setBiz("country", v)} testIdPrefix="mp-biz-country" /></div>
                  <div className="space-y-2"><Label>City</Label><Input value={business.city || ""} onChange={(e) => setBiz("city", e.target.value)} className="h-11 rounded-xl" /></div>
                  <div className="space-y-2"><Label>Website</Label><Input value={business.website || ""} onChange={(e) => setBiz("website", e.target.value)} className="h-11 rounded-xl" /></div>
                  <div className="space-y-2"><Label>Company size</Label>
                    <Select value={business.company_size || ""} onValueChange={(v) => setBiz("company_size", v)}>
                      <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPANY_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Stage</Label>
                    <Select value={business.stage || ""} onValueChange={(v) => setBiz("stage", v)}>
                      <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{BUSINESS_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2 sm:col-span-2"><Label>Description</Label><Textarea value={business.description || ""} onChange={(e) => setBiz("description", e.target.value)} className="min-h-24 rounded-xl" /></div>
                  <div className="space-y-2 sm:col-span-2"><Label>Products / services</Label><Textarea value={business.products_services || ""} onChange={(e) => setBiz("products_services", e.target.value)} className="min-h-20 rounded-xl" /></div>
                </div>
              </div>
            </Section>
          )}

          <Section title="What I offer" subtitle="What can you contribute to the network?" testId="section-offers">
            <ChipToggle options={OFFERS} value={profile.offers || []} onChange={(v) => set("offers", v)} testIdPrefix="mp-offer" />
          </Section>

          <Section title="What I'm looking for" subtitle="What do you want from the network?" testId="section-looking-for">
            <ChipToggle options={LOOKING_FOR} value={profile.looking_for || []} onChange={(v) => set("looking_for", v)} testIdPrefix="mp-looking" />
            <div className="mt-5 space-y-2">
              <Label>Anything specific?</Label>
              <Textarea value={profile.looking_for_details || ""} onChange={(e) => set("looking_for_details", e.target.value)} className="min-h-20 rounded-xl" data-testid="mp-looking-details" />
            </div>
          </Section>

          <Section title="Visibility" testId="section-visibility">
            <p className="mb-3 text-sm text-[color:var(--ink-soft)]">Choose Yes to appear in the public Hub directory when it launches.</p>
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              {[{ id: "yes", label: "Yes, list me" }, { id: "not_yet", label: "Not yet" }].map((o) => (
                <button key={o.id} type="button" onClick={() => set("discoverable_preference", o.id)} data-testid={`mp-visibility-${o.id}`}
                  className={`rounded-2xl border p-4 text-sm font-semibold transition-all ${profile.discoverable_preference === o.id ? "border-[color:var(--invert-bg)] bg-[color:var(--soft)]" : "border-[color:var(--hairline)] bg-[color:var(--surface)] hover:border-[color:var(--ink)]"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Membership" testId="section-membership">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-xl font-semibold text-[color:var(--ink)]" data-testid="mp-membership-tier">{(profile.membership_tier || "free").replace(/^./, (c) => c.toUpperCase())}</p>
                <p className="text-sm text-[color:var(--ink-soft)]">Membership changes are managed via Admin. No payments in this stage.</p>
              </div>
              <a href="/membership" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[color:var(--ink)] underline-offset-4 hover:underline">Compare plans</a>
            </div>
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}
